import {
  BACKEND_API_BASE_PATH,
  MATCHBUS_SESSION_COOKIE_NAME,
} from "@/constants/constants";
import { addYears, toDate } from "date-fns";
import { Bus } from "@/types/bus";
import { ActiveBusRequest, BusRequest } from "@/types/busrequest";
import {
  Company,
  CompanyType,
  CompanyWithAdvertiserLocations,
} from "@/types/company";
import { Feedback } from "@/types/feedback";
import {
  ActiveAdvertiserlocations,
  AdvertiserlocationCreationData,
  AdvertiserLocationEditRequest,
  BusEditRequest,
  BusRequestCreateData,
  BusRequestEditData,
  CompanyEditRequestData,
  HttpResponse,
  MediadataEditRequest,
  PaginatedHttpResponse,
  Unsubscription,
  UnsubscriptionGroup,
  PassiveCompanyData,
  UserEditRequestData,
  UserRegisterData,
  CursorPaginatedHttpResponse,
} from "@/types/http";
import { UploadType } from "@/types/upload";
import {
  AdvertiserLocation,
  AdvertiserLocationWithCompany,
  GeoLocation,
  GeoLocationWithRadius,
} from "@/types/location";
import { Mediadata, MediadataType } from "@/types/mediadata";
import {
  EditEmailSubscriptionSetting,
  EmailSettingsData,
  EmailSettingsTravelservice,
  User,
  UserWithCompany,
} from "@/types/user";
import {
  CombinedRegisterData,
  CreatePaymentResult,
} from "@/types/zodRegisterConsts";
import { CurrentStripeSubscriptionReturn } from "@/app/api/stripe/getCurrentSubscription/[subscriptionId]/route";
import { env } from "@/env.mjs";
import { FilterUnion } from "@/types/UtilTypes";

const NEXT_API_URL = "/api";

export const EndpointName = {
  Users: "user",
  BusRequests: "busrequest",
  Busses: "busses",
  Feedback: "feedback",
  Companies: "company",
  AdvertiserLocations: "advertiserlocation",
  BusRequestArchive: "busrequestarchive",
  Drivers: "driver",
  Newsletter: "newsletter",
} as const;

export async function SignedURL(
  uploadType: UploadType,
  hash: string,
  contentType: string,
): Promise<HttpResponse<string>> {
  const request = new Request(`${BACKEND_API_BASE_PATH}/upload/signed-upload`, {
    method: "POST",
    body: JSON.stringify({
      uploadType: uploadType,
      name: hash,
      contentType: contentType,
    }),
  });
  const response = await fetch(request);
  const json = (await response.json()) as HttpResponse<string>;
  return json;
}

export async function GetUserData(): Promise<UserWithCompany | undefined> {
  if (!document.cookie.includes(MATCHBUS_SESSION_COOKIE_NAME)) {
    return undefined;
  }
  const request = new Request(`${NEXT_API_URL}/${EndpointName.Users}`, {
    method: "GET",
    next: {
      revalidate: 600,
    },
  });
  const response = await fetch(request);
  if (response.ok) {
    return (await response.json()) as UserWithCompany;
  }
  return undefined;
}

export async function ResetPassword(
  username: string,
): Promise<HttpResponse<never>> {
  const request = new Request(`${BACKEND_API_BASE_PATH}/requestresetpw`, {
    method: "POST",
    body: JSON.stringify({
      username: username,
    }),
    cache: "no-store",
  });
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<never>;
}

export async function DoResetPassword(
  userId: string,
  newPassword: string,
  token: string,
): Promise<HttpResponse<never>> {
  const request = new Request(`${BACKEND_API_BASE_PATH}/resetpw`, {
    method: "POST",
    body: JSON.stringify({
      userId: userId,
      token: token,
      newPassword: newPassword,
    }),
    cache: "no-store",
  });
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<never>;
}

export async function RequestAccountDelete(
  email: string,
): Promise<HttpResponse<never>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Users}/requestdelete`,
    {
      method: "POST",
      body: JSON.stringify({
        email: email,
      }),
      cache: "no-store",
    },
  );
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<never>;
}

export async function DeleteAccount(
  userId: string,
  token: string,
): Promise<HttpResponse<never>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Users}/delete`,
    {
      method: "POST",
      body: JSON.stringify({
        userId: userId,
        token: token,
      }),
    },
  );
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<never>;
}

export async function ChangePassword(
  userId: string,
  oldPassword: string,
  newPassword: string,
): Promise<HttpResponse<never>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Users}/${userId}/changepassword`,
    {
      method: "PUT",
      body: JSON.stringify({
        newpassword: newPassword,
        oldpassword: oldPassword,
      }),
    },
  );
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<never>;
}

export async function UpdateUser(
  updateData: UserEditRequestData,
): Promise<HttpResponse<User>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Users}`,
    {
      method: "PUT",
      body: JSON.stringify(updateData),
      cache: "no-store",
    },
  );
  const response = await fetch(request);
  const respObj = (await response.json()) as HttpResponse<User[]>;
  return respObj.successful
    ? { ...respObj, entity: respObj.entity[0] }
    : respObj;
}

export async function UpdateUserById(
  id: string,
  updateData: UserEditRequestData,
): Promise<HttpResponse<User>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Users}/${id}`,
    {
      method: "PUT",
      body: JSON.stringify(updateData),
      cache: "no-store",
    },
  );
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<User>;
}

export async function GetRegisteredCompaniesNear<
  CT extends readonly CompanyType[] | undefined,
>(
  companyTypes?: CT,
  locations?: GeoLocationWithRadius[],
): Promise<HttpResponse<Company<FilterUnion<CompanyType, CT>>[]>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Companies}/registerednear`,
    {
      method: "POST",
      body: JSON.stringify({
        locations: !!locations && locations.length > 0 ? locations : undefined,
        companyTypes:
          !!companyTypes && companyTypes.length > 0 ? companyTypes : undefined,
      }),
      next: {
        revalidate: 600,
      },
    },
  );
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<
    Company<FilterUnion<CompanyType, CT>>[]
  >;
}

export async function CreatePassiveCompany<
  CT extends Extract<CompanyType, "busoperator" | "touroperator"> =
    "busoperator",
>(company: PassiveCompanyData<CT>): Promise<HttpResponse<Company>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Companies}`,
    {
      method: "POST",
      body: JSON.stringify(company),
      cache: "no-store",
    },
  );
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<Company>;
}

export async function UpdateCompany<CT extends CompanyType = CompanyType>(
  id: string,
  updateData: CompanyEditRequestData,
): Promise<HttpResponse<Company<CT>>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Companies}/${id}`,
    {
      method: "PUT",
      body: JSON.stringify(updateData),
      cache: "no-store",
    },
  );
  const response = await fetch(request);
  const responseObj = (await response.json()) as HttpResponse<Company<CT>[]>;
  return responseObj.successful
    ? {
        ...responseObj,
        entity: responseObj.entity[0],
      }
    : responseObj;
}

export async function RemoveCompanyPicture(
  id: string,
): Promise<HttpResponse<Company<"travelService">>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Companies}/${id}/startpagepicture`,
    {
      method: "DELETE",
      cache: "no-store",
    },
  );
  const response = await fetch(request);
  const responseObj = (await response.json()) as HttpResponse<
    Company<"travelService">[]
  >;
  return responseObj.successful
    ? {
        ...responseObj,
        entity: responseObj.entity[0],
      }
    : responseObj;
}

export async function GetCompany<CT extends CompanyType = CompanyType>(
  id: string,
): Promise<HttpResponse<Company<CT>>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Companies}/${id}`,
    {
      method: "GET",
      cache: "no-store",
    },
  );
  console.log("Fetching company " + id);
  const response = await fetch(request);
  const respObj = (await response.json()) as HttpResponse<Company<CT>[]>;
  return respObj.successful
    ? {
        ...respObj,
        entity: respObj.entity[0],
      }
    : respObj;
}

export async function GetActiveAdvertisers(): Promise<
  HttpResponse<Company<"travelService">[]>
> {
  const request = new Request(
    `${env.NEXT_PUBLIC_SITE_URL}${BACKEND_API_BASE_PATH}/${EndpointName.Companies}/activeadvertisers`,
    {
      method: "GET",
      next: {
        revalidate: 600,
      },
    },
  );
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<Company<"travelService">[]>;
}

export async function GetActiveAdvertiserlocationsWithCompanies(): Promise<
  HttpResponse<ActiveAdvertiserlocations>
> {
  const request = new Request(
    `${env.NEXT_PUBLIC_SITE_URL}${BACKEND_API_BASE_PATH}/${EndpointName.AdvertiserLocations}/activeall`,
    {
      method: "GET",
      next: {
        revalidate: 600,
      },
    },
  );
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<ActiveAdvertiserlocations>;
}

export async function GetAdvertiserLocationsWithCompanyNear(
  startLocation?: GeoLocation,
  endLocation?: GeoLocation,
): Promise<HttpResponse<AdvertiserLocationWithCompany[]>> {
  const request = new Request(
    `${env.NEXT_PUBLIC_SITE_URL}${BACKEND_API_BASE_PATH}/${EndpointName.AdvertiserLocations}/activeallnear`,
    {
      method: "POST",
      body: JSON.stringify({
        start:
          !startLocation ||
          Number.isNaN(startLocation.latitude) ||
          Number.isNaN(startLocation.longitude)
            ? undefined
            : startLocation,
        end:
          !endLocation ||
          Number.isNaN(endLocation.latitude) ||
          Number.isNaN(endLocation.longitude)
            ? undefined
            : endLocation,
      }),
      next: {
        revalidate: 600,
      },
    },
  );
  const response = await fetch(request);
  const json = (await response.json()) as HttpResponse<
    AdvertiserLocationWithCompany[]
  >;
  return json;
}

export async function GetCompanyWithAdvertiserLocations(
  page: number,
  itemsPerPage: number,
  filterA?: Record<string, unknown>,
  filterO?: Record<string, unknown>[],
): Promise<CursorPaginatedHttpResponse<CompanyWithAdvertiserLocations[]>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Companies}/withlocations/?take=${itemsPerPage}&skip=${
      (page - 1) * itemsPerPage
    }${
      filterA && filterO
        ? `&filter=${encodeURIComponent(JSON.stringify({ a: filterA, o: filterO }))}`
        : ""
    }`,
    {
      method: "GET",
      next: {
        revalidate: 600,
      },
    },
  );
  const response = await fetch(request);
  const result = (await response.json()) as CursorPaginatedHttpResponse<
    CompanyWithAdvertiserLocations[]
  >;
  console.log("Fetched advertiser locations with company", result);
  return result;
}

export async function GetUserRelevantBusRequests(): Promise<
  HttpResponse<BusRequest[]>
> {
  const startTime = new Date();
  const startString = startTime.getTime();
  const endTime = addYears(startTime, 1).getTime();
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.BusRequests}/filter/relevant`,
    {
      method: "POST",
      body: JSON.stringify({
        startTime: startString,
        endTime: endTime,
      }),
    },
  );
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<BusRequest[]>;
}

export async function GetBusrequest(
  id: string,
): Promise<HttpResponse<BusRequest>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.BusRequests}/${id}`,
    {
      method: "GET",
      next: {
        revalidate: 600,
      },
    },
  );
  const response = await fetch(request);
  const responseObj = (await response.json()) as HttpResponse<BusRequest[]>;
  return responseObj.successful
    ? {
        ...responseObj,
        entity: responseObj.entity[0],
      }
    : responseObj;
}

export async function GetMatchesForRequest(
  id: string,
): Promise<HttpResponse<BusRequest[]>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.BusRequests}/filter/match/${id}`,
    {
      method: "GET",
      next: {
        revalidate: 600,
      },
    },
  );
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<BusRequest[]>;
}

export async function GetAdvertiserRequests(
  near: GeoLocation[],
): Promise<HttpResponse<ActiveBusRequest[]>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.BusRequests}/filter/nearbyrequests`,
    {
      method: "POST",
      body: JSON.stringify({
        Locations: near,
      }),
      next: {
        revalidate: 600,
      },
    },
  );
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<ActiveBusRequest[]>;
}

export async function GetBusRequestFromCompany(
  id: string,
): Promise<HttpResponse<BusRequest[]>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.BusRequests}/filter/company/${id}`,
    {
      method: "GET",
      next: {
        revalidate: 600,
      },
    },
  );
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<BusRequest[]>;
}

export async function GetBussesForCompany(
  id: string,
): Promise<HttpResponse<Bus[]>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Busses}/company/${id}`,
    {
      method: "GET",
      next: {
        revalidate: 600,
      },
    },
  );
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<Bus[]>;
}

export async function AddRequest(
  request: BusRequestCreateData,
): Promise<HttpResponse<BusRequest>> {
  const r = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.BusRequests}`,
    {
      method: "POST",
      body: JSON.stringify(request),
      cache: "no-store",
    },
  );
  const response = await fetch(r);
  return (await response.json()) as HttpResponse<BusRequest>;
}

export async function UpdateRequest(
  id: string,
  updateData: BusRequestEditData,
): Promise<HttpResponse<BusRequest>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.BusRequests}/${id}`,
    {
      method: "PUT",
      body: JSON.stringify(updateData),
      cache: "no-store",
    },
  );
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<BusRequest>;
}

export async function AddBus(
  bus: BusEditRequest,
): Promise<HttpResponse<Bus[]>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Busses}`,
    {
      method: "POST",
      body: JSON.stringify(bus),
      next: {
        revalidate: 600,
      },
    },
  );
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<Bus[]>;
}

export async function GetMyEmailSettingsClient(): Promise<
  HttpResponse<EmailSettingsData | EmailSettingsTravelservice>
> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Users}/email/settings`,
    {
      method: "GET",
    },
  );
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<
    EmailSettingsData | EmailSettingsTravelservice
  >;
}

export async function UpdateBus(
  id: string,
  bus: BusEditRequest,
): Promise<HttpResponse<Bus[]>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Busses}/${id}`,
    {
      method: "PUT",
      body: JSON.stringify(bus),
      cache: "no-store",
    },
  );
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<Bus[]>;
}

export async function RemoveBus(bus: Bus): Promise<HttpResponse<never>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Busses}/${bus.id}`,
    {
      method: "DELETE",
    },
  );
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<never>;
}

export async function SendFeedback(
  feedback: Feedback,
): Promise<HttpResponse<never>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Feedback}`,
    {
      method: "POST",
      body: JSON.stringify(feedback),
    },
  );
  const response = await fetch(request);
  if (response.status === 200) {
    const json = (await response.json()) as HttpResponse<never>;
    return json;
  } else {
    return { successful: false, error: "", errordesc: "" };
  }
}

export async function GetAdvertiserLocations(): Promise<
  HttpResponse<AdvertiserLocation[]>
> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.AdvertiserLocations}`,
    {
      method: "GET",
      next: {
        revalidate: 600,
      },
    },
  );
  const response = await fetch(request);
  if (response.status === 200) {
    const json = (await response.json()) as HttpResponse<AdvertiserLocation[]>;
    return json;
  } else {
    return { successful: false, error: "", errordesc: "" };
  }
}

export async function GetAdvertiserLocationsForCompany(
  companyId: string,
): Promise<HttpResponse<AdvertiserLocation[]>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.AdvertiserLocations}/company/${companyId}`,
    {
      method: "GET",
      next: {
        revalidate: 600,
      },
    },
  );
  const response = await fetch(request);
  if (response.status === 200) {
    const json = (await response.json()) as HttpResponse<AdvertiserLocation[]>;
    return json;
  } else {
    return { successful: false, error: "", errordesc: "" };
  }
}

export async function AddAdvertiserLocation(
  location: AdvertiserlocationCreationData,
): Promise<HttpResponse<AdvertiserLocation[]>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.AdvertiserLocations}`,
    {
      method: "POST",
      body: JSON.stringify(location),
      cache: "no-store",
    },
  );
  const response = await fetch(request);
  if (response.status === 200) {
    const json = (await response.json()) as HttpResponse<AdvertiserLocation[]>;
    return json;
  } else {
    return { successful: false, error: "", errordesc: "" };
  }
}

export async function UpdateAdvertiserLocation(
  id: string,
  location: AdvertiserLocationEditRequest,
): Promise<HttpResponse<AdvertiserLocation[]>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.AdvertiserLocations}/${id}`,
    {
      method: "PUT",
      body: JSON.stringify(location),
      cache: "no-store",
    },
  );
  const response = await fetch(request);
  const json = (await response.json()) as HttpResponse<AdvertiserLocation[]>;
  return json;
}

export async function SetMainAdvertiserLocation(
  id: string,
): Promise<HttpResponse<AdvertiserLocation[]>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.AdvertiserLocations}/setmainlocation/${id}`,
    {
      method: "PUT",
      body: JSON.stringify(id),
      cache: "no-store",
    },
  );
  const response = await fetch(request);
  const json = (await response.json()) as HttpResponse<AdvertiserLocation[]>;
  return json;
}

export async function GetMediadata(
  type: MediadataType,
  language: string,
): Promise<HttpResponse<Mediadata[]>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/mediadata/${type}/${language}`,
    {
      method: "GET",
      next: {
        revalidate: 600,
      },
    },
  );
  const response = await fetch(request);
  if (response.status === 200) {
    const json = (await response.json()) as HttpResponse<Mediadata[]>;
    return json;
  } else {
    return { successful: false, error: "", errordesc: "" };
  }
}

export async function UpdateMediadata(
  type: MediadataType,
  value: MediadataEditRequest,
  language: string,
): Promise<HttpResponse<Mediadata>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/mediadata/${type}/${language}`,
    {
      method: "PUT",
      body: JSON.stringify(value),
      cache: "no-store",
    },
  );
  const response = await fetch(request);
  if (response.status === 200) {
    const json = (await response.json()) as HttpResponse<Mediadata>;
    return json;
  } else {
    return { successful: false, error: "", errordesc: "" };
  }
}

export async function RemoveAdvertiserLocation(
  location: AdvertiserLocation,
): Promise<HttpResponse<never>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.AdvertiserLocations}/${location.id}`,
    {
      method: "DELETE",
      cache: "no-store",
    },
  );
  const response = await fetch(request);
  if (response.status === 200) {
    const json = (await response.json()) as HttpResponse<never>;
    return json;
  } else {
    return { successful: false, error: "", errordesc: "" };
  }
}

export async function DeleteBusrequest(
  id: string,
): Promise<HttpResponse<never>> {
  const delRequest = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.BusRequests}/${id}`,
    {
      method: "DELETE",
      cache: "no-store",
    },
  );
  const deleteResponse = await fetch(delRequest);

  return (await deleteResponse.json()) as HttpResponse<never>;
}

export async function GetUserByEmail(
  email: string,
): Promise<HttpResponse<User>> {
  const r = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Users}/query`,
    {
      method: "POST",
      body: JSON.stringify({ email: email }),
      next: {
        revalidate: 600,
      },
    },
  );

  const response = await fetch(r);
  return await response.json();
}

export async function GetAllUnsubscriptions(
  limit?: number,
  offset?: number,
): Promise<Unsubscription[]> {
  const queries =
    "" +
    (limit ? "limit=" + String(limit) : "") +
    (offset ? "&offset=" + String(offset) : "");
  const r = new Request(
    `${BACKEND_API_BASE_PATH}/admin/mailer/unsubscriptions` +
      (queries ? "?" + queries : ""),
    {
      method: "GET",
      next: {
        revalidate: 600,
      },
    },
  );
  const response = await fetch(r);
  if (!response.ok) {
    throw new Error(
      "unable to parse Data: Response was " + String(response.status),
    );
  }
  const jsonResult = (await response.json()) as Unsubscription[];
  return jsonResult;
}

export async function GetAllActiveUnsubscriptions(
  limit?: number,
  offset?: number,
): Promise<Unsubscription[]> {
  const queries =
    "" + (limit ? "limit=" + limit : "") + (offset ? "&offset=" + offset : "");
  const r = new Request(
    `${BACKEND_API_BASE_PATH}/admin/mailer/unsubscriptions/active` +
      (queries ? "?" + queries : ""),
    {
      method: "GET",
      next: {
        revalidate: 600,
      },
    },
  );
  const response = await fetch(r);
  if (!response.ok) {
    throw new Error("unable to parse Data: Response was " + response.status);
  }
  const jsonResult = (await response.json()) as Unsubscription[];
  return jsonResult;
}

export async function QueryGlobalUnsubscriptions(
  searchTerm: string,
): Promise<Unsubscription[]> {
  const req = new Request(
    `${BACKEND_API_BASE_PATH}/admin/mailer/unsubscriptions/search/${searchTerm}`,
    {
      method: "GET",
      next: {
        revalidate: 600,
      },
    },
  );
  const resp = await fetch(req);
  if (!resp.ok) {
    throw new Error(
      "Unable to query Unsubscritpions: Reponse was " + String(resp.status),
    );
  }
  const jsonResult = (await resp.json()) as Unsubscription[];
  return jsonResult;
}

export async function DeleteUnsubscription(email: string): Promise<boolean> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/admin/mailer/unsubscriptions/${encodeURIComponent(
      email,
    )}`,
    {
      method: "DELETE",
      cache: "no-store",
    },
  );
  const response = await fetch(request);
  return response.ok;
}

export async function GetAllUnsubscriptionGroups(): Promise<
  UnsubscriptionGroup[] | undefined
> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/admin/mailer/unsubscriptions/groups`,
    {
      next: {
        revalidate: 60,
      },
    },
  );
  const response = await fetch(request);
  if (!response.ok) {
    return undefined;
  }
  return (await response.json()) as UnsubscriptionGroup[];
}

export async function DeleteUnsubscriptionFromGroup(
  groupId: string,
  email: string,
): Promise<boolean> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/admin/mailer/unsubscriptions/groups/${encodeURIComponent(
      groupId,
    )}/${encodeURIComponent(email)}`,
    {
      method: "DELETE",
      next: {
        revalidate: 600,
      },
    },
  );
  const response = await fetch(request);
  return response.ok;
}

export async function GetStripePaymentData(
  data: CombinedRegisterData,
): Promise<CreatePaymentResult> {
  console.log("Sending body: " + JSON.stringify(data));
  const request = new Request(`${NEXT_API_URL}/stripe/create-payment`, {
    body: JSON.stringify(data),
    method: "POST",
    cache: "no-store",
  });

  const resp = await fetch(request);

  if (resp.ok) {
    const result = (await resp.json()) as Promise<CreatePaymentResult>;
    return result;
  }
  const res = (await resp.json()) as string;
  console.log("Got error " + res);
  throw new Error(res);
}

// Not in use anymore
/*export async function UpdateStripePaymentData(
  data: UpdateIntentData,
): Promise<PaymentIntentData> {
  console.log("Sending body: " + JSON.stringify(data));
  const request = new Request(`${NEXT_API_URL}/stripe/create-payment`, {
    body: JSON.stringify(data),
    method: "PATCH",
  });

  const resp = await fetch(request);
  if (resp.ok) {
    const result = (await resp.json()) as Promise<PaymentIntentData>;
    return result;
  }
  const error = await resp.json() as string;
  console.log("Got error "+error);
  throw new Error(error);
}*/

// Fetch a request and retry if it failed with a non 200 or 401 statuscode

export async function AdminGetAllRequests(
  page: number,
  itemsPerPage: number,
  filterA?: Record<string, unknown>,
  filterO?: Record<string, unknown>[],
): Promise<
  CursorPaginatedHttpResponse<Extract<BusRequest, { state: "active" }>[]>
> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/admin/${EndpointName.BusRequests}?take=${itemsPerPage}&skip=${
      (page - 1) * itemsPerPage
    }${
      filterA && filterO
        ? `&filter=${encodeURIComponent(JSON.stringify({ a: filterA, o: filterO }))}`
        : ""
    }`,
    {
      method: "GET",
      next: {
        revalidate: 600,
      },
    },
  );
  console.log("JSON: " + JSON.stringify({ a: filterA, o: filterO }));
  const response = await fetch(request);
  const results = (await response.json()) as CursorPaginatedHttpResponse<
    Extract<BusRequest, { state: "active" }>[]
  >;
  if (results.successful && !results.entity) {
    results.entity = [];
  }
  return results;
}

export async function AdminGetPendingSingleRequests(
  page: number,
  itemsPerPage: number,
  filterA?: Record<string, unknown>,
  filterO?: Record<string, unknown>[],
): Promise<CursorPaginatedHttpResponse<BusRequest[]>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/admin/${EndpointName.BusRequests}/pending?take=${itemsPerPage}&skip=${
      (page - 1) * itemsPerPage
    }${
      filterA && filterO
        ? `&filter=${encodeURIComponent(JSON.stringify({ a: filterA, o: filterO }))}`
        : ""
    }`,
    {
      method: "GET",
      next: {
        revalidate: 600,
      },
    },
  );
  console.log("JSON: " + JSON.stringify({ a: filterA, o: filterO }));
  const response = await fetch(request);
  const results = (await response.json()) as CursorPaginatedHttpResponse<
    BusRequest[]
  >;
  if (results.successful && !results.entity) {
    results.entity = [];
  }
  return results;
}

export async function GetCompanies<CT extends CompanyType = CompanyType>(
  page: number,
  itemsPerPage: number,
  filterA?: Record<string, unknown>,
  filterO?: Record<string, unknown>[],
): Promise<CursorPaginatedHttpResponse<Company<CT>[]>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Companies}?take=${itemsPerPage}&skip=${
      (page - 1) * itemsPerPage
    }${
      filterA && filterO
        ? `&filter=${encodeURIComponent(JSON.stringify({ a: filterA, o: filterO }))}`
        : ""
    }`,
    {
      method: "GET",
      next: {
        revalidate: 600,
      },
    },
  );
  const response = await fetch(request);
  return (await response.json()) as CursorPaginatedHttpResponse<Company<CT>[]>;
}

export async function CountRegisteredCompanies(
  page: number,
  itemsPerPage: number,
  filterA?: Record<string, unknown>,
  filterO?: Record<string, unknown>[],
): Promise<CursorPaginatedHttpResponse<never[]>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Companies}/countregistered?take=${itemsPerPage}&skip=${
      (page - 1) * itemsPerPage
    }${
      filterA && filterO
        ? `&filter=${encodeURIComponent(JSON.stringify({ a: filterA, o: filterO }))}`
        : ""
    }`,
    {
      method: "GET",
      next: {
        revalidate: 600,
      },
    },
  );
  const response = await fetch(request);
  return (await response.json()) as CursorPaginatedHttpResponse<never[]>;
}

export async function ConfirmUser(
  token: string,
  userId: string,
): Promise<HttpResponse<never>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Users}/${userId}/confirm?token=${token}`,
    {
      method: "GET",
    },
  );
  const response = await fetch(request);
  const json = (await response.json()) as HttpResponse<never>;
  return json;
}

export async function Register(
  data: UserRegisterData,
): Promise<HttpResponse<never>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Users}`,
    {
      method: "POST",
      body: JSON.stringify(data),
      cache: "no-store",
    },
  );
  const response = await fetch(request);
  const json = (await response.json()) as HttpResponse<never>;
  return json;
}

export async function EditUserEmailSettings(
  values: EditEmailSubscriptionSetting,
): Promise<HttpResponse<never>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Users}/email/settings`,
    {
      method: "POST",
      body: JSON.stringify(values),
    },
  );
  const response = await fetch(request);
  return (await response.json()) as HttpResponse<never>;
}

export async function UnsubscribeUserEmailGlobal(
  email: string,
  enabled: boolean,
): Promise<HttpResponse<never>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Users}/email/settings`,
    {
      method: "POST",
      body: JSON.stringify({ enabled: enabled, email: email }),
    },
  );
  const response = await fetch(request);
  const json = (await response.json()) as HttpResponse<never>;
  return json;
}

export async function UnsubscribeUserEmailGroup(
  email: string,
  enabled: boolean,
  group: string,
): Promise<HttpResponse<never>> {
  const request = new Request(
    `${BACKEND_API_BASE_PATH}/${EndpointName.Users}/email/settings`,
    {
      method: "POST",
      body: JSON.stringify({ enabled: enabled, email: email, group: group }),
    },
  );
  const response = await fetch(request);
  const json = (await response.json()) as HttpResponse<never>;
  return json;
}

export async function GetCurrentSubscriptionForUser(
  subscriptionId: string,
  abortSignal?: AbortSignal,
): Promise<CurrentStripeSubscriptionReturn> {
  const request = new Request(
    `${NEXT_API_URL}/stripe/getCurrentSubscription/${subscriptionId}`,
    {
      method: "GET",
      signal: abortSignal,
    },
  );

  const resp = await fetch(request);
  if (!resp.ok) {
    const errorMessage = await resp.json();
    throw new Error(errorMessage);
  }

  return (await resp.json()) as CurrentStripeSubscriptionReturn;
}
